import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { InvoiceDisplayStatusV2 } from 'anchor-scout-common/types';
import { useOverlayContext } from 'seagull';
import { Copy, CreditCardSwap, DocumentCheckmark, Download, MoneyReceive, PaperclipAttachment, Redo, Send, Sync, Void } from 'seagull/icons';
import { getRefundActionConfig, allowResendAction, allowVoidAction, allowUnvoidAction, allowUnmarkPaidExternallyAction } from '~/business-logic/invoiceLogic';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useDownloadInvoicePdf } from '~/hooks/invoice/useDownloadInvoicePdf';
import { useDownloadReceiptPdf } from '~/hooks/invoice/useDownloadReceiptPdf';
import { useCopyDirectLink } from '~/hooks/useCopyDirectLink';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { usePaidExternally } from '~/hooks/usePaidExternally';
import { useResendInvoice } from '~/hooks/useResendInvoice';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { invoiceModel, relationshipModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import { makeResumePaymentInvoiceAction, utilsTextsPath } from '../FullPageInvoice.utils';
import { useCanResyncQbo } from './useCanResyncQbo';
import { useCanResyncXero } from './useCanResyncXero';
import { useResyncQbo } from './useResyncQbo';
import { useResyncXero } from './useResyncXero';
export function isReceiptEligibleStatus(status) {
    return invoiceModel.SHARED_RECEIPT_ELIGIBLE_STATUSES.has(status);
}
export const FullPageInvoiceHeaderActionsTestIds = pathgen('FullPageInvoiceHeaderActionsTestIds');
export function useFullPageInvoiceHeaderActions(args) {
    const { t: additionalActionsTexts } = useLocaleKeys('components.fullPageInvoice.additionalActions');
    const { t: utilsTexts } = useLocaleKeys(utilsTextsPath);
    const { t: invoiceTexts } = useLocaleKeys('invoice');
    const { invoice, relationship, isVendor, reloadInvoice, onStopPayment, onResumePayment, onVoidInvoice, onUnvoidInvoice, onUnmarkPaidExternally, onRequestRefund, onAttachFilesClick, isUploadingAttachments, hideDropdownActions } = args;
    const { native } = useIoContext();
    const { shouldShowMarkAsPaid, markAsPaid } = usePaidExternally();
    const { isResendingInvoice, resendInvoice } = useResendInvoice();
    const { isFetchingLink, copyDirectLink } = useCopyDirectLink();
    const { logBI } = useStoreActions();
    const isMemberUser = useStore(selectors.user.isMemberUser);
    const isClientMember = !isVendor && isMemberUser;
    const { isDownloadingInvoicePdf, downloadInvoicePdf } = useDownloadInvoicePdf();
    const { downloadReceiptPdf } = useDownloadReceiptPdf();
    const { PAYMENT_RECEIPT_PDF, AD_HOC_INVOICING } = useFeatureFlags();
    const history = useHistory();
    const isTerminated = relationshipModel.isTerminated(relationship);
    const isAdHoc = !!(AD_HOC_INVOICING && invoice.isAdHoc);
    const { showConfirmAction } = useOverlayContext();
    const refundActionConfig = getRefundActionConfig({
        invoice,
        isVendor
    });
    const isInvoiceDisputed = invoiceModel.displayStatus(invoice) === InvoiceDisplayStatusV2.Disputed;
    const showReceiptItem = PAYMENT_RECEIPT_PDF && isReceiptEligibleStatus(invoiceModel.displayStatus(invoice));
    const showQboIntegration = invoice.integration.isSynced() && !invoice.integration.isRefundRecorded() && !isInvoiceDisputed;
    const showXeroIntegration = invoice.xeroIntegration.isPaymentRecorded() && !isInvoiceDisputed;
    const showResyncQboIntegration = showQboIntegration && isVendor;
    const showResyncXeroIntegration = showXeroIntegration && isVendor;
    const { canResyncQbo, refreshQbo } = useCanResyncQbo({
        enabled: !!showResyncQboIntegration,
        invoiceId: invoice.id
    });
    const { canResyncXero, refreshXero } = useCanResyncXero({
        enabled: !!showResyncXeroIntegration,
        invoiceId: invoice.id
    });
    const { resyncQbo } = useResyncQbo({
        onSettled: ()=>refreshQbo(),
        isPaymentRecorded: invoice.integration.isPaymentRecorded()
    });
    const { resyncXero } = useResyncXero({
        onSettled: ()=>refreshXero()
    });
    const disabledResyncTooltip = 'Paid invoices already included in a synced payout can’t be resynced';
    const isResyncQboIntegrationDisabled = isClientMember || !canResyncQbo;
    const isResyncXeroIntegrationDisabled = isClientMember || !canResyncXero;
    const handleResync = (param)=>{
        let { isPaymentRecorded, isInvoiceRecorded, agreementName, onConfirm } = param;
        let content;
        if (isPaymentRecorded && isInvoiceRecorded) {
            content = "A new invoice and payment will be created in ".concat(agreementName, ". The re-sync will only succeed if the previous invoice and payment are first deleted from ").concat(agreementName, ". Please ensure they are removed before continuing.");
        } else if (isPaymentRecorded) {
            content = "A new payment will be created in ".concat(agreementName, ". The re-sync will only succeed if the previous payment is first deleted from ").concat(agreementName, ". Please ensure it is removed before continuing.");
        } else if (isInvoiceRecorded) {
            content = "A new invoice will be created in ".concat(agreementName, ". The re-sync will only succeed if the previous invoice is first deleted from ").concat(agreementName, ". Please ensure it is removed before continuing.");
        } else {
            return;
        }
        showConfirmAction({
            biName: 'resync_invoice_to_accounting_confirm',
            title: "Re-sync to ".concat(agreementName),
            content,
            primaryButtonLabel: 'Re-sync',
            secondaryButtonLabel: 'Cancel',
            width: '610px',
            onConfirm
        });
    };
    const handleResyncQbo = ()=>{
        handleResync({
            isPaymentRecorded: invoice.integration.isPaymentRecorded(),
            isInvoiceRecorded: invoice.integration.isPaymentRecorded() || invoice.integration.isInvoiceRecorded(),
            agreementName: getAccountingDetails(Accounting.QBO).fullName,
            onConfirm: ()=>resyncQbo(invoice.id)
        });
    };
    const handleResyncXero = ()=>{
        const integration = invoice.xeroIntegration.getIntegration();
        handleResync({
            isPaymentRecorded: integration.isPaymentRecorded(),
            isInvoiceRecorded: false,
            agreementName: 'Xero',
            onConfirm: ()=>resyncXero(invoice.id)
        });
    };
    const inline = compact([
        {
            text: additionalActionsTexts.downloadPdf(),
            testId: 'fpi-download-pdf',
            intent: Intent.InvoiceDownload,
            disabled: isDownloadingInvoicePdf || isClientMember || !invoice.pdfExists,
            tooltipTitle: invoice.pdfExists ? undefined : 'Generating PDF',
            iconStart: /*#__PURE__*/ _jsx(Download, {}),
            onClick: ()=>downloadInvoicePdf(invoice.id),
            bi: {
                name: 'download_pdf'
            }
        }
    ]);
    const dropdownItems = compact([
        allowResendAction(invoiceModel.displayStatus(invoice), isVendor) && {
            text: additionalActionsTexts.resendInvoice(),
            testId: 'fpi-resend-invoice',
            intent: Intent.InvoiceResend,
            disabled: isResendingInvoice || isClientMember,
            iconStart: /*#__PURE__*/ _jsx(Send, {}),
            onClick: ()=>resendInvoice(invoice).then(()=>reloadInvoice()),
            bi: {
                name: 'resend_invoice'
            }
        },
        {
            text: additionalActionsTexts.copyLink(),
            testId: 'fpi-copy-link',
            intent: Intent.InvoiceCopyLink,
            disabled: isFetchingLink || isClientMember,
            iconStart: /*#__PURE__*/ _jsx(Copy, {}),
            onClick: ()=>{
                logBI(14001);
                copyDirectLink(relationship.id, {
                    type: 'invoice',
                    id: invoice.id,
                    isAdHoc
                });
            },
            bi: {
                name: 'copy_link'
            }
        },
        showReceiptItem && {
            text: additionalActionsTexts.downloadPaymentReceipt(),
            testId: FullPageInvoiceHeaderActionsTestIds.MoreActions.DownloadPaymentReceipt(),
            iconStart: /*#__PURE__*/ _jsx(MoneyReceive, {}),
            onClick: ()=>downloadReceiptPdf(invoice.id),
            bi: {
                name: 'download_payment_receipt'
            }
        },
        !native.isNative && {
            text: additionalActionsTexts.viewAgreement(),
            testId: 'fpi-view-agreement',
            disabled: isClientMember || isAdHoc,
            tooltipTitle: isAdHoc ? invoiceTexts.adHocTooltip() : undefined,
            iconStart: /*#__PURE__*/ _jsx(DocumentCheckmark, {}),
            onClick: ()=>history.push(routes.agreementMinisiteRoutes(relationshipModel.id(relationship)).agreement),
            bi: {
                name: 'view_agreement'
            }
        },
        shouldShowMarkAsPaid(invoiceModel.displayStatus(invoice), invoiceModel.paymentDetails(invoice), isVendor) && {
            text: additionalActionsTexts.markAsPaidExternally(),
            testId: 'fpi-mark-paid-externally',
            intent: Intent.InvoiceMarkPaidExternally,
            disabled: isClientMember,
            iconStart: /*#__PURE__*/ _jsx(MoneyReceive, {}),
            onClick: ()=>markAsPaid(invoice, ()=>reloadInvoice()),
            bi: {
                name: 'mark_paid_externally'
            }
        },
        refundActionConfig.showAction && {
            text: refundActionConfig.text,
            testId: 'fpi-request-refund',
            intent: refundActionConfig.intent,
            iconStart: /*#__PURE__*/ _jsx(CreditCardSwap, {}),
            onClick: onRequestRefund,
            tooltipTitle: refundActionConfig.tooltipTitle,
            disabled: refundActionConfig.disabled,
            bi: {
                name: 'request_refund'
            }
        },
        showResyncQboIntegration && {
            bi: {
                name: 'resync_invoice_to_qbo'
            },
            text: "Re-sync to ".concat(getAccountingDetails(Accounting.QBO).fullName),
            testId: FullPageInvoiceHeaderActionsTestIds.MoreActions.ResyncQboInvoices(),
            disabled: isResyncQboIntegrationDisabled,
            tooltipTitle: !canResyncQbo ? disabledResyncTooltip : undefined,
            iconStart: /*#__PURE__*/ _jsx(Sync, {}),
            onClick: handleResyncQbo
        },
        showResyncXeroIntegration && {
            bi: {
                name: 'resync_invoice_to_xero'
            },
            text: additionalActionsTexts.reSyncPaymentToXero(),
            testId: FullPageInvoiceHeaderActionsTestIds.MoreActions.ResyncXeroInvoices(),
            disabled: isResyncXeroIntegrationDisabled,
            tooltipTitle: !canResyncXero ? disabledResyncTooltip : undefined,
            iconStart: /*#__PURE__*/ _jsx(Sync, {}),
            onClick: handleResyncXero
        },
        isVendor && {
            bi: {
                name: 'attach_files_to_invoice'
            },
            text: additionalActionsTexts.attachFiles(),
            testId: FullPageInvoiceHeaderActionsTestIds.MoreActions.AttachFiles(),
            iconStart: /*#__PURE__*/ _jsx(PaperclipAttachment, {}),
            onClick: onAttachFilesClick,
            disabled: isUploadingAttachments
        },
        allowUnvoidAction(invoice, isVendor) && {
            text: additionalActionsTexts.unvoidInvoice(),
            testId: 'fpi-unvoid-invoice',
            intent: Intent.InvoiceUnvoid,
            iconStart: /*#__PURE__*/ _jsx(Redo, {}),
            onClick: onUnvoidInvoice,
            bi: {
                name: 'unvoid_invoice'
            }
        },
        allowUnmarkPaidExternallyAction(invoice, isVendor) && {
            text: additionalActionsTexts.unmarkAsPaidExternally(),
            testId: 'fpi-unmark-paid-externally',
            intent: Intent.InvoiceUnmarkPaidExternally,
            iconStart: /*#__PURE__*/ _jsx(Redo, {}),
            onClick: onUnmarkPaidExternally,
            bi: {
                name: 'unmark_paid_externally'
            }
        },
        !isVendor && invoiceModel.isPaymentStopped(invoice) && _object_spread_props(_object_spread({}, makeResumePaymentInvoiceAction(isTerminated, utilsTexts)), {
            testId: 'fpi-resume-payment',
            iconStart: /*#__PURE__*/ _jsx(MoneyReceive, {}),
            onClick: onResumePayment,
            bi: {
                name: 'resume_payment'
            }
        }),
        !isVendor && invoiceModel.isPending(invoice) && {
            text: additionalActionsTexts.stopPayment(),
            intent: Intent.InvoiceStopPayment,
            disabled: isTerminated,
            tooltipTitle: isTerminated ? utilsTexts.terminated() : undefined,
            testId: 'fpi-stop-payment',
            skin: 'error',
            iconStart: /*#__PURE__*/ _jsx(Void, {}),
            onClick: onStopPayment,
            bi: {
                name: 'stop_payment'
            }
        },
        allowVoidAction(invoiceModel.displayStatus(invoice), invoiceModel.paymentDetails(invoice), isVendor) && {
            text: additionalActionsTexts.voidInvoice(),
            testId: 'fpi-void-invoice',
            intent: Intent.InvoiceVoid,
            skin: 'error',
            iconStart: /*#__PURE__*/ _jsx(Void, {}),
            onClick: onVoidInvoice,
            bi: {
                name: 'void_invoice'
            }
        }
    ]);
    return {
        inline,
        dropdown: hideDropdownActions ? [] : dropdownItems
    };
}
